import { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import LoadingButton from '../LoadingButton';

const CANCELLATION_REASONS = [
  'Reduced financial capacity',
  'Change in personal circumstances',
  'Lack of communication/updates',
  'Unsure of my donation\'s impact',
  'Concern regarding fund management',
  'Set up by mistake or forgotten donation',
  'Supporting a different cause/charity',
  'Campaign goal reached',
  'Loss of trust',
  'Feeling unappreciated',
  'Irrelevant projects or content',
  'Other - please specify'
];

function CancellationReasonModal({
  show,
  onHide,
  handleConfirm,
  loading,
  title,
  confirmText = 'Confirm',
  staffName
}) {
  const [selectedReason, setSelectedReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [notes, setNotes] = useState('');
  const [validated, setValidated] = useState(false);

  const isOther = selectedReason === 'Other - please specify';

  const handleClose = () => {
    setSelectedReason('');
    setOtherReason('');
    setNotes('');
    setValidated(false);
    onHide();
  };

  const handleSubmit = () => {
    setValidated(true);

    if (!selectedReason) return;
    if (isOther && !otherReason.trim()) return;

    const reason = isOther ? `Other: ${otherReason.trim()}` : selectedReason;
    handleConfirm(reason, notes.trim() || null);
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3">
        <div className="mb-3 d-flex gap-3">
          <div>
            <small className="text-muted d-block">Staff Member</small>
            <span className="fw-semi-bold">{staffName || '-'}</span>
          </div>
          <div>
            <small className="text-muted d-block">Date</small>
            <span className="fw-semi-bold">
              {dayjs().format('DD MMM YYYY, HH:mm')}
            </span>
          </div>
        </div>

        <Form.Group className="mb-3">
          <Form.Label>
            Reason <span className="text-danger">*</span>
          </Form.Label>
          <Form.Select
            value={selectedReason}
            onChange={e => {
              setSelectedReason(e.target.value);
              if (e.target.value !== 'Other - please specify') {
                setOtherReason('');
              }
            }}
            isInvalid={validated && !selectedReason}
          >
            <option value="">Select a reason...</option>
            {CANCELLATION_REASONS.map(reason => (
              <option key={reason} value={reason}>
                {reason}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            Please select a reason.
          </Form.Control.Feedback>
        </Form.Group>

        {isOther && (
          <Form.Group className="mb-3">
            <Form.Label>
              Please specify <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter reason..."
              value={otherReason}
              onChange={e => setOtherReason(e.target.value)}
              isInvalid={validated && isOther && !otherReason.trim()}
            />
            <Form.Control.Feedback type="invalid">
              Please specify a reason.
            </Form.Control.Feedback>
          </Form.Group>
        )}

        {selectedReason && (
          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Add any additional notes..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-danger" onClick={handleClose}>
          Cancel
        </Button>
        <LoadingButton loading={loading} disabled={loading} onClick={handleSubmit}>
          {confirmText}
        </LoadingButton>
      </Modal.Footer>
    </Modal>
  );
}

CancellationReasonModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  handleConfirm: PropTypes.func,
  loading: PropTypes.bool,
  title: PropTypes.string,
  confirmText: PropTypes.string,
  staffName: PropTypes.string
};

export default CancellationReasonModal;
